// The sessions list (D79; Console D `#/sessions`, APS-314): sessions that started in the window,
// filtered by app, release, outcome, errors, replay, device, OS version, issue, network endpoint,
// screen, where they stopped or feature flag, sorted by start, length or errors (APS-301) and
// paged by cursor. Each opens the session view. The filters live in the URL, so a filtered list
// can be shared. The strip and the segments' counts come from the same filters (sessions/summary).
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useState } from 'react'
import {
  appsQuery,
  fetchSessions,
  projectsQuery,
  type SessionFilters,
  sessionCountsQuery,
} from '../api/queries'
import type { SessionCounts, SessionListItem } from '../api/types'
import { DebugOnlyLine } from '../components/DebugOnly'
import {
  Btn,
  EmptyNote,
  HovRow,
  Kbd,
  PageHead,
  SectionHead,
  Seg,
  SelectChip,
  SortTh,
  Strip,
  type StripItem,
  Table,
  TableFoot,
} from '../components/d'
import { Icon } from '../components/icons'
import { SessionPath } from '../components/SessionPath'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { OUTCOME } from '../lib/conventions'
import { duration, shortId } from '../lib/format'
import {
  formatSessionQuery,
  parseSessionQuery,
  SEARCH_KEYS,
  type SearchError,
} from '../lib/sessionSearch'
import { useProjectParams } from './params'
import { SessionFilterChips } from './SessionFilterChips'
import { carried, rangeDays, type SESSION_SORTS, type SessionsSearch } from './search'

/** A session's outcome as a pill: the word, in its state color (the session page, until APS-315). */
export function OutcomePill({ outcome }: { outcome: string }) {
  const { t } = useI18n()
  switch (outcome) {
    case OUTCOME.crashed:
      return <span className="pill crash">{t('session.outcome.crashed')}</span>
    case OUTCOME.abnormalExit:
      return <span className="pill warn">{t('session.outcome.abnormal')}</span>
    case OUTCOME.exited:
      return <span className="pill ok">{t('session.outcome.exited')}</span>
    case OUTCOME.active:
      return <span className="pill neutral">{t('session.outcome.active')}</span>
    default:
      return <span className="pill neutral">{t('session.outcome.unknown')}</span>
  }
}

/** The outcomes a row shows, as Console D's glyph and the product's word (D103 #14). */
const OUTCOME_WORD = {
  // The segment's word, so a row reads as the filter that lists it (Mosaic, APS-314).
  crashed: { glyph: '✕', word: 'sessions.outcome.crashed' },
  abnormal_exit: { glyph: '○', word: 'session.outcome.abnormal' },
  exited: { glyph: '✓', word: 'session.outcome.exited' },
  active: { glyph: '●', word: 'session.outcome.active' },
  unknown: { glyph: '–', word: 'session.outcome.unknown' },
} as const satisfies Record<string, { glyph: string; word: MessageId }>

/** `✕ ended in a crash`: the glyph is decoration, a screen reader hears the word. */
export function Outcome({ outcome }: { outcome: string }) {
  const { t } = useI18n()
  const key = (outcome in OUTCOME_WORD ? outcome : 'unknown') as keyof typeof OUTCOME_WORD
  const o = OUTCOME_WORD[key]
  return (
    <span className={`us-o ${key}`}>
      <span aria-hidden="true">{o.glyph} </span>
      {t(o.word)}
    </span>
  )
}

/** The API's filters from the URL's. */
export function sessionFilters(s: SessionsSearch): SessionFilters & { days: number } {
  return {
    app_id: s.app,
    release: s.release,
    outcome: s.outcome,
    device_model: s.device,
    os_version: s.os,
    build: s.build,
    has_error: s.errors,
    has_replay: s.replay,
    issue_id: s.issue,
    ...(s.method && s.host && s.template
      ? {
          endpoint: {
            method: s.method,
            host: s.host,
            template: s.template,
            ...(s.failed ? { failed: true } : {}),
          },
        }
      : {}),
    ...(s.screen
      ? {
          screen: s.screen,
          ...(s.min_ttid !== undefined ? { min_ttid_ms: s.min_ttid } : {}),
          ...(s.min_ttfd !== undefined ? { min_ttfd_ms: s.min_ttfd } : {}),
        }
      : {}),
    ...(s.failed_request ? { has_failed_request: true } : {}),
    ...(s.flag ? { flag: s.flag, flag_variant: s.variant } : {}),
    ...(s.stopped ? { stopped: s.stopped } : {}),
    ...(s.user ? { user: s.user } : {}),
    days: rangeDays(s.range, 'sessions'),
  }
}

/** Three segments over the list (APS-196, Console D): how the session went, errors, replay. */
const OUTCOME_SEG = ['all', 'crashed', 'abnormal_exit', 'ok'] as const
const ERRORS_SEG = ['any', 'with', 'without'] as const
const REPLAY_SEG = ['any', 'with'] as const
type OutcomeSeg = (typeof OUTCOME_SEG)[number]
type ErrorsSeg = (typeof ERRORS_SEG)[number]
type ReplaySeg = (typeof REPLAY_SEG)[number]

/** Where each segment is, from the URL. */
export function segmentsOf(s: SessionsSearch): {
  outcome: OutcomeSeg
  errors: ErrorsSeg
  replay: ReplaySeg
} {
  return {
    outcome: s.outcome ?? 'all',
    errors: s.errors === true ? 'with' : s.errors === false ? 'without' : 'any',
    replay: s.replay ? 'with' : 'any',
  }
}

const OUTCOME_SEG_LABEL: Record<OutcomeSeg, MessageId> = {
  all: 'sessions.segment.all',
  crashed: 'sessions.outcome.crashed',
  abnormal_exit: 'sessions.outcome.abnormal_exit',
  ok: 'sessions.outcome.ok',
}
const ERRORS_SEG_LABEL: Record<ErrorsSeg, MessageId> = {
  any: 'sessions.chip.any',
  with: 'sessions.chip.withErrors',
  without: 'sessions.chip.withoutErrors',
}
const REPLAY_SEG_LABEL: Record<ReplaySeg, MessageId> = {
  any: 'sessions.chip.any',
  with: 'sessions.chip.withReplay',
}

/**
 * The segments' counts, from the counts over the other filters. A segment's counts hold only
 * while the other two segments are open (any); otherwise none are shown, never a wrong one.
 */
export function segmentCounts(
  c: SessionCounts | undefined,
  seg: ReturnType<typeof segmentsOf>,
): {
  outcome?: Record<OutcomeSeg, number>
  errors?: Record<ErrorsSeg, number>
  replay?: Record<ReplaySeg, number>
} {
  if (!c) return {}
  const open = {
    outcome: seg.outcome === 'all',
    errors: seg.errors === 'any',
    replay: seg.replay === 'any',
  }
  return {
    ...(open.errors && open.replay
      ? {
          outcome: {
            all: c.sessions,
            crashed: c.crashed,
            abnormal_exit: c.abnormal_exit,
            ok: c.ok,
          },
        }
      : {}),
    ...(open.outcome && open.replay
      ? {
          errors: {
            any: c.sessions,
            with: c.with_errors,
            without: Math.max(0, c.sessions - c.with_errors),
          },
        }
      : {}),
    ...(open.outcome && open.errors ? { replay: { any: c.sessions, with: c.with_replay } } : {}),
  }
}

const SEARCH_ERROR: Record<SearchError['kind'], MessageId> = {
  unknown: 'sessions.search.error.unknown',
  bare: 'sessions.search.error.bare',
  value: 'sessions.search.error.value',
  twice: 'sessions.search.error.twice',
  quote: 'sessions.search.error.quote',
}

/** The query field: `key:value` terms over the list's filters, applied on Enter. */
function SessionSearchBox({
  search,
  onApply,
}: {
  search: SessionsSearch
  onApply: (patch: Partial<SessionsSearch>) => void
}) {
  const { t } = useI18n()
  const id = useId()
  // Device, OS and flag are chips (SessionFilterChips): the text holds every other term, so the
  // two never disagree; typing one of them turns it into its chip.
  const current = formatSessionQuery(search, { chips: false })
  const [draft, setDraft] = useState(current)
  const [error, setError] = useState<SearchError>()
  // On a phone the key:value hint hides behind a "?" (styles/sessions.css, APS-383).
  const [hint, setHint] = useState(false)
  useEffect(() => {
    setDraft(current)
    setError(undefined)
  }, [current])
  return (
    <form
      className={hint ? 'us-q hint' : 'us-q'}
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        const parsed = parseSessionQuery(draft)
        if (!parsed.ok) {
          setError(parsed.error)
          return
        }
        setError(undefined)
        const q = parsed.query
        const patch: Partial<SessionsSearch> = {
          release: q.release,
          // A chip's key stays as it is unless the text names it.
          device: q.device ?? search.device,
          os: q.os ?? search.os,
          outcome: q.outcome,
          screen: q.screen,
          // A slow-load bound belongs to its screen.
          ...(q.screen !== search.screen ? { min_ttid: undefined, min_ttfd: undefined } : {}),
          issue: q.issue,
          ...(q.flag ? { flag: q.flag, variant: q.variant } : {}),
        }
        // The chip terms leave the text even when the rest of it reads as before (APS-414), so
        // `current` may not change and the effect above would not reset the draft.
        setDraft(formatSessionQuery({ ...search, ...patch }, { chips: false }))
        onApply(patch)
      }}
    >
      <label htmlFor={id} className="visually-hidden">
        {t('sessions.search.label')}
      </label>
      <input
        id={id}
        type="search"
        className="input"
        value={draft}
        placeholder={t('sessions.search.placeholder')}
        translate="no"
        spellCheck={false}
        autoComplete="off"
        maxLength={1000}
        aria-invalid={error ? true : undefined}
        aria-describedby={`${id}-hint`}
        onChange={(e) => setDraft(e.target.value)}
      />
      <Btn size="sm" type="submit">
        {t('sessions.search.apply')}
      </Btn>
      <button
        type="button"
        className="us-qt"
        aria-expanded={hint}
        aria-controls={`${id}-hint`}
        aria-label={t('sessions.search.hintToggle')}
        onClick={() => setHint(!hint)}
      >
        ?
      </button>
      {error ? (
        <p id={`${id}-hint`} role="alert" className="us-qh err">
          {t(SEARCH_ERROR[error.kind], {
            key: 'key' in error ? error.key : '',
            word: 'word' in error ? error.word : '',
            keys: SEARCH_KEYS.join(', '),
          })}
        </p>
      ) : (
        <p id={`${id}-hint`} className="us-qh">
          {t('sessions.search.hint', { keys: SEARCH_KEYS.join(', ') })}
        </p>
      )}
    </form>
  )
}

/**
 * A segment with its counts beside each value, when they are known. `phoneSelect`: on a phone
 * (≤ 640 px, styles/sessions.css) the same choice is a select chip instead, for words too long
 * for one line of segments (Mosaic, APS-383); a segment never wraps to a second row.
 */
function CountSeg<T extends string>({
  label,
  options,
  labels,
  counts,
  value,
  onChange,
  phoneSelect,
}: {
  label: string
  options: readonly T[]
  labels: Record<T, MessageId>
  counts: Record<T, number> | undefined
  value: T
  onChange: (v: T) => void
  phoneSelect?: boolean
}) {
  const i18n = useI18n()
  const { t } = i18n
  return (
    <span className={phoneSelect ? 'us-g has-sel' : 'us-g'}>
      <span className="us-l" aria-hidden="true">
        {label}
      </span>
      {phoneSelect ? (
        <SelectChip label={label} value={value} onChange={(v) => onChange(v as T)}>
          {options.map((o) => (
            <option key={o} value={o}>
              {t(labels[o])}
            </option>
          ))}
        </SelectChip>
      ) : null}
      <Seg
        label={label}
        value={value}
        onChange={onChange}
        options={options.map((o) => ({
          value: o,
          label: (
            <>
              {t(labels[o])}
              {counts ? <span className="us-n">{compactCount(i18n, counts[o])}</span> : null}
            </>
          ),
        }))}
      />
    </span>
  )
}

/** A removable filter chip from a link: `screen: Checkout ×`. */
function LinkChip({
  name,
  value,
  clear,
  onClear,
}: {
  name: string
  value: string
  clear: string
  onClear: () => void
}) {
  return (
    <span className="chip">
      <span>{name}:</span>
      <span translate="no">{value}</span>
      <button type="button" aria-label={clear} onClick={onClear}>
        <Icon name="x" small />
      </button>
    </span>
  )
}

function length(i18n: Translator, s: SessionListItem): string {
  return duration(i18n, Math.max(0, s.duration_ms))
}

/** A count as the strip and the segments write it: 1,268 below ten thousand, 312.4k above. */
export function compactCount(i18n: Translator, n: number): string {
  return n < 10_000
    ? i18n.number(n)
    : i18n
        .number(n, { notation: 'compact', minimumFractionDigits: 1, maximumFractionDigits: 1 })
        // Console D writes thousands as `k` (312.4k), as the mock does.
        .replace(/K$/, 'k')
}

/** A share in percent: two decimals, or two significant digits under 1 % (never a false 0). */
export function sharePercent(i18n: Translator, n: number, of: number): string {
  if (of <= 0 || n <= 0) return i18n.number(0)
  const pct = (100 * n) / of
  return pct < 1
    ? i18n.number(pct, { maximumSignificantDigits: 2 })
    : i18n.number(pct, { maximumFractionDigits: 2 })
}

/** The strip over the filters: sessions, crashed, median length, with replay (APS-301). */
function sessionStrip(i18n: Translator, c: SessionCounts): StripItem[] {
  const { t } = i18n
  const share = (n: number) => sharePercent(i18n, n, c.sessions)
  return [
    { dt: t('sessions.strip.sessions'), dd: compactCount(i18n, c.sessions) },
    {
      dt: t('sessions.strip.crashed'),
      dd: share(c.crashed),
      small: '%',
      d: t('sessions.strip.count', { count: c.crashed, n: compactCount(i18n, c.crashed) }),
    },
    {
      dt: t('sessions.strip.median'),
      dd: c.duration_ms.p50 === null ? '–' : duration(i18n, c.duration_ms.p50),
      ...(c.duration_ms.p90 !== null
        ? { d: t('sessions.strip.p90', { length: duration(i18n, c.duration_ms.p90) }) }
        : {}),
    },
    {
      dt: t('sessions.strip.replay'),
      dd: compactCount(i18n, c.with_replay),
      small: t('sessions.strip.unit', { count: c.with_replay }),
      d: t('sessions.strip.of', { count: c.sessions, n: compactCount(i18n, c.sessions) }),
    },
  ]
}

export function SessionsPage() {
  const i18n = useI18n()
  const { t, number, relative, date } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as SessionsSearch
  const navigate = useNavigate()
  const filters = sessionFilters(search)
  const sort = { sort: search.sort ?? 'started', order: search.order ?? 'desc' } as const
  const seg = segmentsOf(search)
  // The counts over every filter but the segments' own.
  const scope = sessionFilters({
    ...search,
    outcome: undefined,
    errors: undefined,
    replay: undefined,
  })
  const counts = useQuery(sessionCountsQuery(projectId, scope))
  const segCounts = segmentCounts(counts.data, seg)
  const sessions = useInfiniteQuery({
    queryKey: ['project', projectId, 'sessions', filters, sort],
    queryFn: ({ pageParam }) => fetchSessions(projectId, { ...filters, ...sort }, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  })
  const rows = sessions.data?.pages.flatMap((p) => p.sessions) ?? []
  // The j/k cursor: the row with focus, the first until one is focused (the view's one accent).
  const [cur, setCur] = useState(0)
  const listKey = JSON.stringify([filters, sort])
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new list starts on its first row
  useEffect(() => setCur(0), [listKey])
  const set = (patch: Partial<SessionsSearch>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) })
  const carry = carried(search)
  const open = (s: SessionListItem) =>
    navigate({
      to: '/o/$orgId/p/$projectId/sessions/$sessionId',
      params: { orgId, projectId, sessionId: s.session_id },
      search: carry,
    })
  const sortBy = (k: (typeof SESSION_SORTS)[number]) =>
    set(
      sort.sort === k
        ? { order: sort.order === 'desc' ? 'asc' : undefined }
        : { sort: k === 'started' ? undefined : k, order: undefined },
    )
  const sortTh = (k: (typeof SESSION_SORTS)[number], label: MessageId, num?: boolean) => (
    <SortTh on={sort.sort === k} onSort={() => sortBy(k)} {...(num ? { className: 'num' } : {})}>
      {t(label)}
    </SortTh>
  )
  const projects = useQuery(projectsQuery(orgId))
  const apps = useQuery(appsQuery(orgId, projectId))
  const app = apps.data?.find((a) => a.id === search.app)
  const meta = [
    projects.data?.find((p) => p.id === projectId)?.name,
    app ? appLabel(app) : t('shell.allAppsShort'),
    search.release ?? t('sessions.meta.allReleases'),
    filters.days === 1 ? t('issues.chip.day') : t('issues.chip.days', { days: filters.days }),
  ]
    // An app named as its project reads once: Shop · iOS, not Shop · Shop · iOS.
    .flatMap((part) => (part ? part.split(' · ') : []))
    .filter((part, i, all) => part !== all[i - 1])
    .join(' · ')

  return (
    <main id="main" className="pg us-pg">
      <PageHead
        crumb={
          search.issue ? (
            <>
              <Link to="/o/$orgId/p/$projectId/issues" params={{ orgId, projectId }} search={carry}>
                {t('nav.issues')}
              </Link>
              {' / '}
              <Link
                to="/o/$orgId/p/$projectId/issues/$issueId"
                params={{ orgId, projectId, issueId: search.issue }}
                search={carry}
                translate="no"
              >
                {shortId(search.issue, 8, 4)}
              </Link>
              {` / ${t('nav.sessions')}`}
            </>
          ) : undefined
        }
        title={t('nav.sessions')}
        meta={<span translate="no">{meta}</span>}
      />
      {counts.data ? <Strip items={sessionStrip(i18n, counts.data)} /> : null}
      <section className="sec us-list" aria-labelledby="us-list-h">
        <SectionHead
          id="us-list-h"
          title={t('sessions.section')}
          meta={
            sessions.isPending
              ? undefined
              : t('sessions.shown', { count: rows.length, more: sessions.hasNextPage ? 1 : 0 })
          }
          right={
            <span className="hints">
              <span>
                <kbd translate="no">j</kbd>
                <kbd translate="no">k</kbd> {t('d.hint.move')}
              </span>
              <span>
                <Kbd>↵</Kbd> {t('d.hint.open')}
              </span>
            </span>
          }
        />
        {/* biome-ignore lint/a11y/useSemanticElements: a row of controls, not a form's fieldset */}
        <div className="us-fl" role="group" aria-label={t('sessions.filters')}>
          <SessionSearchBox search={search} onApply={set} />
          <CountSeg
            label={t('sessions.chip.outcome')}
            options={OUTCOME_SEG}
            labels={OUTCOME_SEG_LABEL}
            counts={segCounts.outcome}
            value={seg.outcome}
            phoneSelect
            onChange={(v) => set({ outcome: v === 'all' ? undefined : v })}
          />
          <CountSeg
            label={t('sessions.chip.errors')}
            options={ERRORS_SEG}
            labels={ERRORS_SEG_LABEL}
            counts={segCounts.errors}
            value={seg.errors}
            onChange={(v) => set({ errors: v === 'any' ? undefined : v === 'with' })}
          />
          <CountSeg
            label={t('sessions.chip.replay')}
            options={REPLAY_SEG}
            labels={REPLAY_SEG_LABEL}
            counts={segCounts.replay}
            value={seg.replay}
            onChange={(v) => set({ replay: v === 'with' ? true : undefined })}
          />
          <span className="us-links">
            {search.issue ? (
              <LinkChip
                name={t('sessions.chip.issue')}
                value={shortId(search.issue, 8, 4)}
                clear={t('sessions.chip.clearIssue')}
                onClear={() => set({ issue: undefined })}
              />
            ) : null}
            {search.method && search.host && search.template ? (
              <LinkChip
                name={
                  search.failed ? t('sessions.chip.failedEndpoint') : t('sessions.chip.endpoint')
                }
                value={`${search.method} ${search.host}${search.template}`}
                clear={t('sessions.chip.clearEndpoint')}
                onClear={() =>
                  set({
                    method: undefined,
                    host: undefined,
                    template: undefined,
                    failed: undefined,
                  })
                }
              />
            ) : null}
            {search.screen ? (
              <LinkChip
                name={
                  search.min_ttfd !== undefined
                    ? t('sessions.chip.slowScreenTtfd', { ms: search.min_ttfd })
                    : search.min_ttid !== undefined
                      ? t('sessions.chip.slowScreen', { ms: search.min_ttid })
                      : t('sessions.chip.screen')
                }
                value={search.screen}
                clear={t('sessions.chip.clearScreen')}
                onClear={() => set({ screen: undefined, min_ttid: undefined, min_ttfd: undefined })}
              />
            ) : null}
            {search.failed_request ? (
              <LinkChip
                name={t('sessions.chip.request')}
                value={t('sessions.chip.failedRequest')}
                clear={t('sessions.chip.clearFailedRequest')}
                onClear={() => set({ failed_request: undefined })}
              />
            ) : null}
            {search.stopped ? (
              <LinkChip
                name={t('sessions.chip.stopped')}
                value={search.stopped}
                clear={t('sessions.chip.clearStopped')}
                onClear={() => set({ stopped: undefined })}
              />
            ) : null}
            {search.user ? (
              <LinkChip
                name={t('sessions.chip.user')}
                value={search.user}
                clear={t('sessions.chip.clearUser')}
                onClear={() => set({ user: undefined })}
              />
            ) : null}
            <SessionFilterChips
              projectId={projectId}
              search={search}
              filters={filters}
              onChange={set}
            />
          </span>
        </div>
        {sessions.isPending ? (
          <Loading />
        ) : sessions.isError ? (
          <ErrorState error={sessions.error} retry={() => sessions.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyNote title={t('sessions.empty')}>
            {t('sessions.empty.body')}
            <DebugOnlyLine
              projectId={projectId}
              scope={{ app: search.app, days: filters.days }}
              empty
            />
          </EmptyNote>
        ) : (
          <>
            <Table label={t('nav.sessions')} rowKeys>
              <thead>
                <tr>
                  <th scope="col" className="f">
                    {t('sessions.col.session')}
                  </th>
                  <th scope="col">{t('sessions.col.outcome')}</th>
                  {sortTh('started', 'sessions.col.started')}
                  {sortTh('length', 'sessions.col.length', true)}
                  <th scope="col">{t('releases.col.release')}</th>
                  <th scope="col">{t('sessions.col.device')}</th>
                  <th scope="col">{t('sessions.col.os')}</th>
                  {sortTh('errors', 'sessions.col.errors', true)}
                  <th scope="col" className="last">
                    {t('sessions.col.path')}
                  </th>
                </tr>
              </thead>
              {/* The row with focus is the j/k cursor. */}
              <tbody
                onFocus={(e) => {
                  const tr = (e.target as HTMLElement).closest('tr')
                  const i = tr ? [...(tr.parentElement?.children ?? [])].indexOf(tr) : -1
                  if (i >= 0) setCur(i)
                }}
              >
                {rows.map((s, i) => (
                  <HovRow
                    key={`${s.app_id}-${s.session_id}`}
                    cur={i === cur}
                    onOpen={() => open(s)}
                  >
                    <td className="f">
                      <Link
                        to="/o/$orgId/p/$projectId/sessions/$sessionId"
                        params={{ orgId, projectId, sessionId: s.session_id }}
                        search={carry}
                        className="ttl"
                        translate="no"
                      >
                        {shortId(s.session_id)}
                      </Link>
                      {s.has_replay ? (
                        <span title={t('sessions.replay.label')}>
                          <span className="us-rp-i" aria-hidden="true">
                            <Icon name="play" small />
                          </span>
                          <span className="visually-hidden">{t('sessions.replay.label')}</span>
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <Outcome outcome={s.outcome} />
                    </td>
                    <td>
                      <span title={date(s.started_at, { dateStyle: 'medium', timeStyle: 'short' })}>
                        {relative(s.started_at)}
                      </span>
                    </td>
                    <td className="num">{length(i18n, s)}</td>
                    <td translate="no">{s.release}</td>
                    <td translate="no">{s.device_model || '–'}</td>
                    <td translate="no">{`${s.os_name} ${s.os_version}`.trim() || '–'}</td>
                    <td className={s.error_count ? 'num' : 'num zero'}>{number(s.error_count)}</td>
                    <td className="last us-path">
                      <SessionPath screens={s.path.screens} more={s.path.more} />
                    </td>
                  </HovRow>
                ))}
              </tbody>
            </Table>
            <TableFoot>
              <span>{t('sessions.footer')}</span>
              {sessions.hasNextPage ? (
                <Btn
                  size="sm"
                  disabled={sessions.isFetchingNextPage}
                  onClick={() => sessions.fetchNextPage()}
                >
                  {t('sessions.loadMore')}
                </Btn>
              ) : null}
            </TableFoot>
          </>
        )}
      </section>
    </main>
  )
}
